import Loading from '@/components/Loading';
import { SelectionReorder } from '@/components/Modals/SetupWorkflow/SelectionReorder';
import {
  assembleToggleOptions,
  WORKFLOW_TAB_INDEX,
  workflowTabPath,
} from '@/components/Modals/SetupWorkflow/SetupWorkflow.utils';
import type { SetupWorkflowFormValues } from '@/components/Modals/SetupWorkflow/SetupWorkflow';
import Switch from '@/components/UI/FormElements/Switch';
import type { FC } from 'react';
import { useMemo } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import FormField from '../Forms/FormField';
import { useApiHook } from '@/support/Hooks/api/useApiHook';
import type { SampleType } from '@/model/Sample.model.ts';

const formContextPath = workflowTabPath(WORKFLOW_TAB_INDEX.SAMPLES);
export const Samples: FC = () => {
  const { control } = useFormContext<SetupWorkflowFormValues>();
  const { response: samplesGlossaryResponse, loading: samplesGlossaryLoading } = useApiHook({
    endpoint: 'GET /api/v1/team-glossary',
    path: undefined,
    query: {
      group: 'samples',
      perPage: -1,
      filterType: 'and',
      'filters[]': 'status|eq|active',
      order: 'asc',
    },
    options: { onError: { toast: false, slug: 'samples-glossary-load' } },
  });

  const workflowTabConfig = useWatch({
    control,
    name: 'workflowSetupForm.workflowTabConfig',
  });

  const toggleOptions = useMemo(() => assembleToggleOptions(workflowTabConfig ?? [], 'samples'), [workflowTabConfig]);

  const samples = (samplesGlossaryResponse?.body as { data: Array<SampleType> })?.data ?? [];

  const samplesWithApiId = samples.map((sample) => ({
    ...sample,
    id: sample.api_id,
  }));
  if (samplesGlossaryLoading) {
    return <Loading />;
  }
  return (
    <>
      <Controller
        name={`${formContextPath}.preSelectedSampleTypeIds`}
        control={control}
        render={({ field: { value, onChange } }) => (
          <SelectionReorder
            selectionTitle="sample"
            options={samplesWithApiId}
            selection={value}
            selectionChange={(selection) => onChange(selection)}
          />
        )}
      />
      <FormField label="Auto generate Sample ID">
        <Controller
          defaultValue={true}
          name={`${formContextPath}.autoGenerateSampleIds`}
          control={control}
          render={({ field: { value, onChange } }) => (
            <Switch value={value ?? false} onChange={onChange} testId="auto-generate-sample-id-switch" />
          )}
        />
      </FormField>
      <FormField label="After saving">
        <Controller
          defaultValue="focus-search"
          name={`${formContextPath}.onSave`}
          control={control}
          render={({ field: { value, onChange } }) => (
            <select className="mw5 mb0" value={value} onChange={onChange}>
              <option value="focus-search" data-testid="after-saving-option-focus-search-bar">
                Focus search bar
              </option>
              {Object.keys(toggleOptions).map((k) => (
                <option
                  key={k}
                  value={toggleOptions[k].value}
                  data-testid={`after-saving-option-${toggleOptions[k].value}`}
                >
                  {toggleOptions[k].name}
                </option>
              ))}
              <option value="next-subject" data-testid="after-saving-option-move-to-next-animal">
                Move to next animal
              </option>
            </select>
          )}
        />
      </FormField>
    </>
  );
};
